
import { BRAND_COLLECTIONS, collectionPath } from "@/utils/collections";
const TESTIMONIAL_GROUPS = [
  [
    {
      quote: "The build quality and look is second to none.",
      name: "S. Richardson"
    },
    {
      quote: "Tested it at −17°C at night — it gets more than hot enough.",
      name: "Dan L."
    },
    {
      quote: "We sleep so much better after our evening sessions.",
      name: "Lori H."
    }
  ],
  [
    {
      quote: "An amazing investment for my physical and mental health.",
      name: "Vancouver C."
    },
    {
      quote: "We feel spoiled walking out the backdoor into this luxury.",
      name: "Glenn W."
    },
    {
      quote: "It smells wonderful like cedar — we love it as a family.",
      name: "Joshua T."
    }
  ],
  [
    {
      quote: "Kevin went above and beyond, even on a product he didn’t sell me.",
      name: "Scott R."
    },
    {
      quote: "Worth spending a little more for a high-quality, Canadian-made sauna.",
      name: "Garett G."
    },
    {
      quote: "We use it every night — the relaxation is incredible.",
      name: "Michael B."
    }
  ],
  [
    {
      quote: "Excellent craftsmanship — loving the Canadian cedar down in Florida!",
      name: "Cee S."
    },
    { quote: "The saunas are a piece of art. Love ’em.", name: "Sona C." },
    {
      quote: "Great product, great craftsmanship. You will not find better.",
      name: "Bruno C."
    }
  ]
]

const TESTIMONIAL_INTERVAL = 9000

export default {
  meta: {
    blocks: {
      unwind: {
        public: true,
        dealer: false
      }
    }
  },

  data() {
    return {
      BRAND_COLLECTIONS,
      perfReadMore: false,

      testimonialGroups: TESTIMONIAL_GROUPS,
      activeTestimonial: 0,
      testimonialTimer: null,
      isTestimonialVideoPlaying: false,

      configuratorObserver: null,
      isConfiguratorTapToPlay: false,
      isConfiguratorVideoPlaying: false,

      SEO: {
        // Authored brand-free on purpose: head.titleTemplate appends
        // " - LeisureCraft". Passing the brand name here is what produced
        // "LeisureCraft - LeisureCraft".
        title: "Canadian Made Saunas, Cold Plunges & Hot Tubs",
        description:
          "Saunas, cold plunges, hot tubs and outdoor showers handcrafted in Ontario since 2004. Find your nearest authorized LeisureCraft dealer.",
        // Setting an image also promotes twitter:card to summary_large_image
        // (see mixins/SEO.js) — the homepage was rendering a small square card.
        image: `https://${this.$config.website.domain}/images/leisurecraft-default-share.jpg`,
        index: true,
        canonical: "/",
        options: {
          link: [
            {
              rel: "preload",
              as: "image",
              href: "/images/hero-poster.webp",
              fetchpriority: "high"
            }
          ]
        }
      }
    }
  },

  methods: {
    collectionPath,

    // The hero video is deferred out of the critical window: its <source>
    // elements carry data-src, so nothing downloads until the page has fully
    // loaded. The poster covers the hero in the meantime, so the swap is
    // invisible — main__hero--loaded still fires off the "play" event, just
    // later, which is the intent.
    loadHeroVideo() {
      const video = this.$refs["hero-video"]
      if (!video) return

      video.addEventListener("play", () => {
        this.__log("Hero video is playing")
        this.$refs.hero.classList.add("main__hero--loaded")
      })

      const start = () => {
        video.querySelectorAll("source[data-src]").forEach((source) => {
          source.src = source.dataset.src
          source.removeAttribute("data-src")
        })

        video.load()

        const attempt = video.play()
        if (attempt && attempt.catch) attempt.catch(() => {})
      }

      // onNuxtReady fires after hydration; window "load" waits for the rest of
      // the page assets. Whichever is later is when we want the 1.55 MB fetch.
      if (document.readyState === "complete") start()
      else window.addEventListener("load", start, { once: true })
    },

    playTestimonialVideo() {
      this.isTestimonialVideoPlaying = true
    },

    goToTestimonial(index) {
      this.stopTestimonialRotation()
      this.activeTestimonial = index
      this.startTestimonialRotation()
    },

    startTestimonialRotation() {
      if (this.testimonialTimer || this.testimonialGroups.length <= 1) return

      const prefersReduced =
        typeof window !== "undefined" &&
        window.matchMedia &&
        window.matchMedia("(prefers-reduced-motion: reduce)").matches

      if (prefersReduced) return

      this.testimonialTimer = setInterval(() => {
        this.activeTestimonial = (this.activeTestimonial + 1) % this.testimonialGroups.length
      }, TESTIMONIAL_INTERVAL)
    },

    stopTestimonialRotation() {
      if (this.testimonialTimer) {
        clearInterval(this.testimonialTimer)
        this.testimonialTimer = null
      }
    },

    // Configurator video uses preload="none", so nothing downloads until it
    // plays. Desktop: muted loop that starts when the section scrolls into view
    // and pauses when it leaves. Phones and reduced-motion visitors get the
    // poster with a play button instead, and the video plays on tap.
    setupConfiguratorVideo() {
      if (!this.$refs.configuratorVideo) return

      this.isConfiguratorTapToPlay = window.matchMedia(
        "(hover: none), (max-width: 768px), (prefers-reduced-motion: reduce)"
      ).matches
      if (this.isConfiguratorTapToPlay) return

      if (!("IntersectionObserver" in window)) {
        this.playConfiguratorVideo()
        return
      }

      this.configuratorObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) this.playConfiguratorVideo()
            else if (this.$refs.configuratorVideo) this.$refs.configuratorVideo.pause()
          })
        },
        { threshold: 0.35 }
      )

      this.configuratorObserver.observe(this.$refs.configuratorShot)
    },

    playConfiguratorVideo() {
      const video = this.$refs.configuratorVideo
      if (!video) return

      const attempt = video.play()
      if (attempt && attempt.then) {
        attempt
          .then(() => {
            this.isConfiguratorVideoPlaying = true
          })
          .catch(() => {})
      }
    },

    // 100dvh grows/shrinks as the mobile URL bar hides on scroll, which made
    // the hero and its background media jump. Freeze the viewport height in
    // pixels once at load instead. Re-measure only when the width changes
    // (orientation flip / desktop resize) — height-only resizes are exactly
    // the URL-bar collapse we're ignoring.
    freezeHeroHeight() {
      this.heroViewportWidth = window.innerWidth
      this.$el.style.setProperty("--hero-height", `${window.innerHeight}px`)
    },

    handleViewportResize() {
      if (window.innerWidth !== this.heroViewportWidth) this.freezeHeroHeight()
    }
  },

  mounted() {
    this.freezeHeroHeight()
    window.addEventListener("resize", this.handleViewportResize)
    this.loadHeroVideo()
    this.startTestimonialRotation()
    this.setupConfiguratorVideo()
  },

  beforeDestroy() {
    window.removeEventListener("resize", this.handleViewportResize)
    this.stopTestimonialRotation()

    if (this.configuratorObserver) {
      this.configuratorObserver.disconnect()
      this.configuratorObserver = null
    }
  }
}
